// src/utils/mealPlanGenerator.js
import mealsDb from "../data/meals.json";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * Generate a 7-day meal plan fitted to the user's calorie + protein targets.
 *
 * Each day includes: breakfast, lunch, dinner, 1–2 snacks.
 * Preserves "locked" meals from a prior plan.
 *
 * @param {object} params
 * @param {number} params.calorie_target
 * @param {number} params.protein_target
 * @param {Array}  params.lockedMeals  - array of { dayIndex, slot, meal } to preserve
 * @param {Array}  params.customMeals  - SavedMeal[] from Firestore to include in the pool
 * @returns {Array<DayPlan>} 7-element array
 */
export function generateWeeklyPlan({ calorie_target, protein_target, lockedMeals = [], customMeals = [] }) {
  // Merge built-in + custom meals (adapt custom meal schema)
  const pool = [
    ...mealsDb,
    ...customMeals.map((m) => ({
      id:               m.id,
      name:             m.meal_name,
      meal_type:        m.meal_type || "snack",
      ingredients:      [],
      instructions:     [],
      calories:         m.calories,
      protein:          m.protein,
      carbs:            m.carbs,
      fat:              m.fat,
      prep_time_minutes: 0,
      tags:             ["saved"],
    })),
  ];

  const plan = DAYS.map((dayName, dayIndex) => ({
    dayIndex,
    dayName,
    breakfast: null,
    lunch:     null,
    dinner:    null,
    snacks:    [],
  }));

  // Inject locked meals first
  for (const lock of lockedMeals) {
    const { dayIndex, slot, meal } = lock;
    if (slot === "breakfast" || slot === "lunch" || slot === "dinner") {
      plan[dayIndex][slot] = meal;
    } else if (slot === "snack") {
      plan[dayIndex].snacks = plan[dayIndex].snacks.filter((_, i) => i !== lock.snackIndex);
      plan[dayIndex].snacks[lock.snackIndex || 0] = meal;
    }
  }

  // Fill empty slots
  for (let d = 0; d < 7; d++) {
    const day = plan[d];

    // Budget per slot (approximate)
    const breakfastCal = Math.round(calorie_target * 0.28);
    const lunchCal     = Math.round(calorie_target * 0.32);
    const dinnerCal    = Math.round(calorie_target * 0.30);
    const snackCal     = Math.round(calorie_target * 0.10);

    if (!day.breakfast) {
      day.breakfast = pickMeal(pool, "breakfast", breakfastCal, protein_target * 0.25, []);
    }
    if (!day.lunch) {
      day.lunch = pickMeal(pool, "lunch", lunchCal, protein_target * 0.30, []);
    }
    if (!day.dinner) {
      day.dinner = pickMeal(pool, "dinner", dinnerCal, protein_target * 0.30, []);
    }
    if (day.snacks.length < 1 || !day.snacks[0]) {
      day.snacks[0] = pickMeal(pool, "snack", snackCal, protein_target * 0.15, []);
    }
  }

  return plan;
}

/**
 * Pick the best-fitting meal from the pool for a given slot.
 * Prioritises protein, randomises among top candidates.
 *
 * @param {Array}  pool         - all available meals
 * @param {string} mealType     - "breakfast"|"lunch"|"dinner"|"snack"
 * @param {number} calBudget    - max calories for this slot
 * @param {number} proteinMin   - minimum desired protein
 * @param {Array}  excludeIds   - meal IDs already used today
 * @returns {object|null} meal or null
 */
export function pickMeal(pool, mealType, calBudget, proteinMin, excludeIds = []) {
  const tolerance = 120; // allow 120 cal over budget

  // Filter by type + calorie budget
  let candidates = pool.filter(
    (m) =>
      m.meal_type === mealType &&
      m.calories  <= calBudget + tolerance &&
      !excludeIds.includes(m.id)
  );

  if (candidates.length === 0) {
    // Relax — just match type
    candidates = pool.filter((m) => m.meal_type === mealType && !excludeIds.includes(m.id));
  }
  if (candidates.length === 0) return null;

  // Sort by protein descending, then randomise among top 3
  candidates.sort((a, b) => b.protein - a.protein);
  const topN = candidates.slice(0, Math.min(3, candidates.length));
  return topN[Math.floor(Math.random() * topN.length)];
}

/**
 * Suggest meals for the current moment of day given remaining calories + protein.
 *
 * @param {object} params
 * @param {number} params.remaining_calories
 * @param {number} params.remaining_protein
 * @param {Array}  [params.customMeals]  - SavedMeal[]
 * @returns {Array} up to 3 suggestions
 */
export function suggestMeals({ remaining_calories, remaining_protein, customMeals = [] }) {
  const hour = new Date().getHours();
  let mealType;
  if (hour < 11)       mealType = "breakfast";
  else if (hour < 15)  mealType = "lunch";
  else                 mealType = "dinner";

  const pool = [
    ...mealsDb,
    ...customMeals.map((m) => ({
      id: m.id, name: m.meal_name, meal_type: m.meal_type || "snack",
      ingredients: [], instructions: [],
      calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat,
      prep_time_minutes: 0, tags: ["saved"],
    })),
  ];

  const tolerance = 100;

  // Filter: match time-of-day type or snack; fit within remaining calories
  let candidates = pool.filter(
    (m) =>
      (m.meal_type === mealType || m.meal_type === "snack") &&
      m.calories <= remaining_calories + tolerance
  );

  if (candidates.length === 0) {
    // Fallback: just return highest-protein snacks
    candidates = pool.filter((m) => m.meal_type === "snack");
  }

  // Sort by protein descending
  candidates.sort((a, b) => b.protein - a.protein);

  // Randomly pick up to 3 from top 6
  const topPool = candidates.slice(0, Math.min(6, candidates.length));
  const shuffled = topPool.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 3);
}

/**
 * Aggregate all ingredients from a weekly plan for the grocery list.
 * Returns a flat array of { id, name, quantity, mealName, mealSources, checked }.
 * mealSources tracks every meal that uses this ingredient.
 */
export function aggregateGroceryList(weekPlan) {
  const meals = [];
  for (const day of weekPlan) {
    meals.push(...[day.breakfast, day.lunch, day.dinner, ...(day.snacks || [])].filter(Boolean));
  }
  return aggregateGroceryFromMeals(meals);
}

/**
 * Aggregate ingredients from a flat array of meal objects.
 * Returns deduped array of { id, name, quantity, mealName, mealSources, checked }.
 */
export function aggregateGroceryFromMeals(meals) {
  const map = new Map(); // key = lowercase ingredient name

  for (const meal of meals) {
    if (!meal || !meal.ingredients) continue;
    for (const ing of meal.ingredients) {
      const key = ing.name.toLowerCase();
      if (map.has(key)) {
        const existing = map.get(key);
        if (!existing.mealSources.includes(meal.name)) {
          existing.mealSources.push(meal.name);
        }
      } else {
        map.set(key, {
          id:          `${meal.id}_${ing.name}`,
          name:        ing.name,
          quantity:     ing.quantity,
          mealName:     meal.name,
          mealSources: [meal.name],
          checked:      false,
        });
      }
    }
  }

  return Array.from(map.values());
}

/**
 * Detect repeated meals from a locked meals array.
 * @param {Array} lockedMeals - array of { dayIndex, slot, snackIndex, meal }
 * @returns {Array<{ name, count, days, isBatchCook }>} meals appearing 2+ times
 */
export function detectRepeatedMeals(lockedMeals) {
  const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const mealMap = new Map(); // key = meal name

  for (const lock of lockedMeals) {
    const meal = lock.meal;
    if (!meal) continue;
    const dayName = DAYS[lock.dayIndex] || `Day ${lock.dayIndex + 1}`;
    const key = meal.name;

    if (mealMap.has(key)) {
      const entry = mealMap.get(key);
      entry.count++;
      if (!entry.days.includes(dayName)) entry.days.push(dayName);
      entry.isBatchCook = entry.isBatchCook || (meal.tags || []).includes("batch-cook");
    } else {
      mealMap.set(key, {
        name:        meal.name,
        count:       1,
        days:        [dayName],
        isBatchCook: (meal.tags || []).includes("batch-cook"),
      });
    }
  }

  return Array.from(mealMap.values()).filter((m) => m.count > 1);
}
